import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { useNavigate } from "react-router-dom";
import type { AnswerLink, CatalogueEntry, HandoffTarget, HowToAction } from "../../domain/askNoticeboard/catalogue";
import { reopenPrompt, type TaskRecord } from "../../domain/askNoticeboard/tasks/taskConversation";
import {
  biggestCircleName,
  exampleQuestionNamed,
  pickExampleEntries,
  type AskedQuestion,
} from "../../domain/askNoticeboard/replyTo";
import { phrasingKey } from "../../domain/askNoticeboard/questionMatcher";
import { isIntroductionRequest } from "../../domain/askNoticeboard/introduction";
import { isBareQuestionWord } from "../../domain/askNoticeboard/smallTalk";
import { useAccessibleCircles } from "../../hooks/useAccessibleCircles";
import NoticeboardButton from "../shared/NoticeboardButton";
import type { ChatMessage } from "./AskNoticeboardProvider";
import HandoffCard from "./HandoffCard";
import { EXAMPLE_CARD_STYLES, STARTER_ACTIONS } from "./exampleCards";
import RevealingText from "./RevealingText";
import { useInstantSearch, type SearchResult } from "../../hooks/useInstantSearch";

const COLUMN_MAX_WIDTH = 840;
const TITLE_ID = "ask-noticeboard-title";

/** Enter opens the top result when the typed text is the start of its name, never for a lone "why". */
function shouldOpenTopResult(typed: string, topLabel: string | undefined): boolean {
  const key = typed.trim().toLowerCase();
  const top = topLabel?.toLowerCase();
  if (!top || !key || isBareQuestionWord(key)) return false;
  return top.startsWith(key) && (top === key || !isIntroductionRequest(key));
}

type Props = {
  open: boolean;
  onClose: () => void;
  messages: ChatMessage[];
  available: CatalogueEntry[];
  onSend: (text: string) => void;
  /** An instant search result opened from what was typed: kept in the chat, then the chat closes on it */
  onOpenResult: (typed: string, result: SearchResult) => void;
  /** A reply is still on its way: what's sent now is answered after it */
  isReplying: boolean;
  onNewChat: () => void;
  onRevealed: (id: number) => void;
  /** Open to review: closes the chat and opens the task's screen, filled in */
  onOpenHandoff: (to: HandoffTarget) => void;
  /** The guided task under way, if any: only its own summary offers Looks good and Cancel */
  task: TaskRecord | null;
  /** Looks good: hands the task over and opens its screen */
  onConfirmTask: (to: HandoffTarget) => void;
  /** The handoff follow-up under way, if any: only its own chips stay tappable */
  lastAsked: AskedQuestion | null;
  /** Reopened with this task still in progress: offers Carry on or Start fresh */
  resumeOffer: TaskRecord | null;
  onCarryOn: () => void;
  onStartFresh: () => void;
};

const QuestionChips: React.FC<{
  questions: string[];
  onAsk: (q: string) => void;
  /** From a cancelled or finished task: shown but not tappable */
  disabled?: boolean;
}> = ({ questions, onAsk, disabled }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
    {questions.map((q) => (
      <Chip
        key={q}
        label={q}
        variant="outlined"
        clickable
        disabled={disabled}
        // Chip renders as a div, not a button, so a native "disabled" attribute wouldn't stop the click itself
        onClick={disabled ? undefined : () => onAsk(q)}
        sx={{ borderColor: "divider", color: "text.primary" }}
      />
    ))}
  </Box>
);

/** An answer's own button to the page or record it's about, right-aligned. */
const AnswerLinkButton: React.FC<{ link: AnswerLink; onOpenLink: (href: string, state?: unknown) => void }> = ({
  link,
  onOpenLink,
}) => (
  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
    <NoticeboardButton variant="primary" size="small" onClick={() => onOpenLink(link.href, link.state)}>
      {link.label}
    </NoticeboardButton>
  </Box>
);

/** A how-to's (or recovery's) buttons that do the real thing: open a page, or start a guided task. */
const ActionButtons: React.FC<{
  actions: HowToAction[];
  onOpenHandoff: (to: HandoffTarget) => void;
  onAsk: (q: string) => void;
}> = ({ actions, onOpenHandoff, onAsk }) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
    {actions.map((action) => (
      <NoticeboardButton
        key={action.label}
        variant="secondary"
        size="small"
        onClick={() => (action.kind === "open" ? onOpenHandoff(action.to) : onAsk(action.ask))}
      >
        {action.label}
      </NoticeboardButton>
    ))}
  </Box>
);

/** One example question as a card: the page's icon, the question, and what that page holds. */
const ExampleCard: React.FC<{
  entry: CatalogueEntry;
  /** This Organization's own Circle, named in place of the entry's placeholder, if it names one */
  circleName: string | null;
  onAsk: (q: string) => void;
  prompt?: string;
  description?: string;
}> = ({ entry, circleName, onAsk, prompt, description }) => {
  const { icon: Icon, color, hint } = EXAMPLE_CARD_STYLES[entry.sidebarItem];
  const question = prompt ?? exampleQuestionNamed(entry, circleName);
  const hintId = `ask-noticeboard-example-${entry.id}-${question.replace(/[^a-z0-9]/gi, "-")}`;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onAsk(question)}
      aria-label={question}
      aria-describedby={hintId}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.5, sm: 2 },
        p: { xs: "8px 4px", sm: "6px 16px 6px 8px" },
        width: { xs: "100%", sm: "auto" },
        textAlign: "left",
        font: "inherit",
        color: "text.primary",
        cursor: "pointer",
        bgcolor: (theme) => ({ xs: "background.paper", sm: alpha(theme.palette.colors[color], 0.08) }),
        border: "none",
        borderColor: "divider",
        borderRadius: { xs: 4, sm: 999 },
        boxShadow: "none",
        transition: "box-shadow 150ms, border-color 150ms, transform 150ms",
        "&:hover, &:focus-visible": {
          borderColor: "primary.main",
          boxShadow: (theme) => `0 12px 28px ${alpha(theme.palette.primary.main, 0.16)}`,
          transform: "translateY(-1px)",
          outline: "none",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (theme) => alpha(theme.palette.colors[color], 0.14),
          color: (theme) => theme.palette.colors[color],
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" component="span" sx={{ display: "block", fontWeight: { xs: 400, sm: 600 }, color: { xs: "text.secondary", sm: "text.primary" }, fontSize: { xs: "1rem", sm: "1.1rem" } }}>
          {question}
        </Typography>
        <Typography id={hintId} variant="body2" component="span" sx={{ color: "text.secondary", display: "none" }}>
          {description ?? hint}
        </Typography>
      </Box>

    </Box>
  );
};

/** Opens a task's screen; "Open to review" for one filled in, or a given label for an empty one. */
const OpenToReview: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = "Open to review" }) => (
  <NoticeboardButton size="small" onClick={onClick}>
    {label}
  </NoticeboardButton>
);

/** Reopened with a task still in progress: offers to pick it back up, or drop it. */
const ResumeOfferCard: React.FC<{
  record: TaskRecord;
  onCarryOn: () => void;
  onStartFresh: () => void;
}> = ({ record, onCarryOn, onStartFresh }) => (
  <Box
    data-testid="resume-offer"
    sx={{
      alignSelf: "flex-start",
      maxWidth: "85%",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      px: 2.5,
      py: 2,
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 4,
      boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
    }}
  >
    <Typography variant="body1">{reopenPrompt(record)}</Typography>
    <Box sx={{ display: "flex", gap: 1 }}>
      <NoticeboardButton size="small" onClick={onCarryOn}>
        Carry on
      </NoticeboardButton>
      <NoticeboardButton variant="secondary" size="small" onClick={onStartFresh}>
        Start fresh
      </NoticeboardButton>
    </Box>
  </Box>
);

const AssistantMessage: React.FC<{
  message: Extract<ChatMessage, { role: "assistant" }>;
  onRevealed: () => void;
  onOpenLink: (href: string, state?: unknown) => void;
  onAsk: (q: string) => void;
  onOpenHandoff: (to: HandoffTarget) => void;
  task: TaskRecord | null;
  onConfirmTask: (to: HandoffTarget) => void;
  lastAsked: AskedQuestion | null;
}> = ({ message: { reply, revealed }, onRevealed, onOpenLink, onAsk, onOpenHandoff, task, onConfirmTask, lastAsked }) => (
  <Box
    data-testid="assistant-reply"
    sx={{
      alignSelf: "flex-start",
      maxWidth: "85%",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      px: 2.5,
      py: 2,
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 4,
      boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
    }}
  >
    <RevealingText text={reply.text} animate={!revealed} onDone={onRevealed} />
    {/* Buttons appear once the reply has finished appearing */}
    {revealed && reply.kind === "answer" && (
      <AnswerLinkButton link={reply.link} onOpenLink={onOpenLink} />
    )}
    {revealed && (reply.kind === "unanswered" || reply.kind === "didYouMean" || reply.kind === "introduction") && (
      <QuestionChips questions={reply.questions} onAsk={onAsk} />
    )}
    {revealed && reply.kind === "howTo" && reply.actions.length > 0 && (
      <ActionButtons actions={reply.actions} onOpenHandoff={onOpenHandoff} onAsk={onAsk} />
    )}
    {revealed && reply.kind === "askSlot" && (
      <QuestionChips questions={reply.choices} onAsk={onAsk} disabled={reply.asked !== lastAsked} />
    )}
    {revealed && reply.kind === "recovery" && reply.link && <AnswerLinkButton link={reply.link} onOpenLink={onOpenLink} />}
    {revealed && reply.kind === "recovery" && reply.choices && reply.choices.length > 0 && (
      <QuestionChips questions={reply.choices} onAsk={onAsk} disabled={reply.asked !== lastAsked} />
    )}
    {revealed && reply.kind === "recovery" && reply.actions && reply.actions.length > 0 && (
      <ActionButtons actions={reply.actions} onOpenHandoff={onOpenHandoff} onAsk={onAsk} />
    )}
    {revealed && reply.kind === "cancelled" && reply.choices.length > 0 && (
      <QuestionChips questions={reply.choices} onAsk={onAsk} />
    )}
    {revealed && reply.kind === "handoff" && (
      <HandoffCard summary={reply.summary}>
        <OpenToReview onClick={() => onOpenHandoff(reply.to)} />
      </HandoffCard>
    )}
    {revealed && reply.kind === "task" && reply.choices.length > 0 && (
      <QuestionChips questions={reply.choices} onAsk={onAsk} disabled={reply.task !== task} />
    )}
    {revealed && reply.kind === "task" && reply.summary && reply.to && (
      <HandoffCard summary={reply.summary}>
        {reply.task?.phase === "done" ? (
          <OpenToReview onClick={() => onOpenHandoff(reply.to as HandoffTarget)} />
        ) : (
          // Once the task has moved on (handed over, cancelled, changed), this summary's buttons go
          reply.task === task && (
            <>
              <NoticeboardButton size="small" onClick={() => onConfirmTask(reply.to as HandoffTarget)}>
                Looks good
              </NoticeboardButton>
              <NoticeboardButton variant="secondary" size="small" onClick={() => onAsk("Cancel")}>
                Cancel
              </NoticeboardButton>
            </>
          )
        )}
      </HandoffCard>
    )}
    {revealed && reply.kind === "novaOffer" && (
      <Box>
        <OpenToReview label="Open Nova" onClick={() => onOpenHandoff(reply.to)} />
      </Box>
    )}
    {revealed && reply.kind === "writingRequest" && reply.to && (
      <Box>
        {/* Points to a new, empty Notice, not a filled-in one: "Open to review" would be misleading */}
        <OpenToReview label="Start a Notice" onClick={() => onOpenHandoff(reply.to as HandoffTarget)} />
      </Box>
    )}
  </Box>
);

/** Full-page chat: centred messages, the text box fixed at the bottom. */
const AskNoticeboardModal: React.FC<Props> = ({
  open,
  onClose,
  messages,
  available,
  onSend,
  onOpenResult,
  isReplying,
  onNewChat,
  onRevealed,
  onOpenHandoff,
  task,
  onConfirmTask,
  lastAsked,
  resumeOffer,
  onCarryOn,
  onStartFresh,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("sm"));
  const [draft, setDraft] = useState("");
  const [highlighted, setHighlighted] = useState(-1);
  const endRef = useRef<HTMLDivElement>(null);

  const { results, loading, failed } = useInstantSearch(draft, open);
  const examples = useMemo(() => {
    const actions = STARTER_ACTIONS.flatMap((action) => {
      const entry = available.find((candidate) => candidate.id === action.id);
      return entry ? [{ entry, prompt: action.question, description: action.hint }] : [];
    });
    return actions.length ? actions : pickExampleEntries(available).map((entry) => ({ entry, prompt: undefined, description: undefined }));
  }, [available]);
  const { subcircles } = useAccessibleCircles();
  const circleName = useMemo(() => biggestCircleName(subcircles), [subcircles]);

  // Enter opens the top result when what's typed is the start of its name. "help" asks what Ask Noticeboard
  // can do instead, unless it's the result's whole name ("What is Noticeboard?" is a help article).
  // A lone question word ("why") is a message, even when a title such as "Why Noticeboard" starts with it.
  // While a task is asking for a detail, what's typed is its answer: "Dinner" is the title, not the
  // Notice "Dinner at Artistry". A result still opens when it's clicked or picked with the arrow keys.
  const taskAsking = Boolean(task && task.phase !== "done");
  const pickedWithArrows = useRef(false);
  useEffect(() => {
    pickedWithArrows.current = false;
    setHighlighted(!taskAsking && shouldOpenTopResult(draft, results[0]?.label) ? 0 : -1);
  }, [draft, results, taskAsking]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages, isReplying]);

  const goTo = (href: string, state?: unknown) => {
    setDraft("");
    onClose();
    navigate(href, { state });
  };

  const openResult = (result: SearchResult) => {
    setDraft("");
    onOpenResult(draft, result);
  };

  const ask = (text: string) => {
    // Explicit navigation should not depend on semantic question matching.
    if (
      available.some((entry) => entry.id === "helpCenter.courses") &&
      /^(go to|open|show me|take me to) (the )?courses$/.test(phrasingKey(text))
    ) {
      goTo("/help/courses");
      return;
    }
    setDraft("");
    onSend(text);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" && results.length > 0) {
      e.preventDefault();
      pickedWithArrows.current = true;
      setHighlighted((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp" && results.length > 0) {
      e.preventDefault();
      pickedWithArrows.current = true;
      setHighlighted((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // The field's value, not the last rendered highlight: "why" can still be
      // highlighted from the prefix "wh" until the effect catches up.
      const typedNow = e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement ? e.target.value : draft;
      const chosen = highlighted > 0 || (highlighted === 0 && pickedWithArrows.current) ? results[highlighted] : undefined;
      if (chosen) {
        openResult(chosen);
      } else if (!taskAsking && shouldOpenTopResult(typedNow, results[0]?.label) && results[0]) {
        openResult(results[0]);
      } else {
        ask(typedNow);
      }
    }
  };

  const composer = (
      <Box sx={{ position: "relative", flexShrink: 0, px: { xs: 2, sm: 3 }, pb: "max(12px, env(safe-area-inset-bottom))", pt: 1 }}>
        <Box sx={{ maxWidth: desktop && messages.length === 0 ? 1120 : COLUMN_MAX_WIDTH, mx: "auto" }}>
          {(loading || failed) && (
            <Typography role="status" variant="body2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
              {failed ? "Some results could not be loaded." : "Searching…"}
            </Typography>
          )}
          {results.length > 0 && (
            <List
              dense
              aria-label="Search results"
              sx={{
                mb: 1,
                p: 0.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 4,
              }}
            >
              {results.map((result, i) => (
                <ListItemButton
                  key={result.key}
                  selected={i === highlighted}
                  onClick={() => openResult(result)}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemText primary={result.label} />
                  <Chip label={result.kind} size="small" />
                </ListItemButton>
              ))}
            </List>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              border: "1.5px solid",
              borderColor: "#e2e5eb",
              borderRadius: 999,
              bgcolor: "common.white",
              pl: 2.5,
              pr: 1,
              py: 1,
              boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.primary.main, 0.14)}`,
              "&:focus-within": {
                boxShadow: (theme) =>
                  `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}, 0 12px 32px ${alpha(theme.palette.primary.main, 0.14)}`,
              },
            }}
          >
            <AutoAwesomeOutlinedIcon aria-hidden sx={{ color: "primary.main", display: { xs: "none", sm: "block" } }} />
            <InputBase
              fullWidth
              multiline
              maxRows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about Noticeboard…"
              inputProps={{ "aria-label": "Message" }}
              sx={{ fontSize: "1.0625rem", py: 0.5 }}
            />
            <IconButton
              aria-label="Send"
              onClick={() => ask(draft)}
              disabled={!draft.trim()}
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
                "&.Mui-disabled": {
                  bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.25),
                  color: "primary.contrastText",
                },
              }}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
  );

  return (
    // A plain full-screen Dialog: ActionModal is a centred card with a footer, not a chat with its text box pinned to the bottom
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby={TITLE_ID}
      PaperProps={{
        sx: {
          background: "#fff",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          px: { xs: 1.5, sm: 4 },
          py: { xs: 1, sm: 1.5 },
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
          <Box
            aria-hidden
            sx={{
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              flexShrink: 0,
              borderRadius: "50%",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 20,
              lineHeight: 1,
              pb: "2px",
            }}
          >
            nb
          </Box>
          <Typography id={TITLE_ID} variant="h6" component="h2" sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            Ask Noticeboard
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <NoticeboardButton
            variant="secondary"
            size="small"
            onClick={onNewChat}
            disabled={messages.length === 0}
            sx={{ display: messages.length === 0 ? "none" : "inline-flex" }}
          >
            New chat
          </NoticeboardButton>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ position: "relative", flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 4 } }}>
        <Box sx={{ maxWidth: desktop && messages.length === 0 ? 1120 : COLUMN_MAX_WIDTH, mx: "auto" }}>
          {messages.length === 0 ? (
            <Box sx={{ mt: { xs: "clamp(12px, 5vh, 64px)", md: 6 }, textAlign: "center" }}>
              <Box aria-hidden sx={{ display: "block", width: { xs: "clamp(96px, 17vh, 150px)", sm: "clamp(110px, 20vh, 180px)" }, mx: "auto", mb: 2 }}>
                <svg viewBox="0 0 160 160" fill="none">
                  <circle cx="80" cy="85" r="65" fill="#f0fcfc" />
                  <path d="M80 26v20" stroke="#8ce5e8" strokeWidth="5" />
                  <circle cx="80" cy="20" r="7" fill="#08bdc2" />
                  <rect x="27" y="76" width="18" height="30" rx="9" fill="#ccf6f6" stroke="#86e4e8" />
                  <rect x="115" y="76" width="18" height="30" rx="9" fill="#ccf6f6" stroke="#86e4e8" />
                  <rect x="36" y="48" width="88" height="82" rx="38" fill="white" stroke="#86e4e8" strokeWidth="2" />
                  <rect x="45" y="70" width="70" height="46" rx="23" fill="#101d26" />
                  <path d="M59 92c0-9 12-9 12 0m18 0c0-9 12-9 12 0" stroke="#03c7ca" strokeWidth="3" strokeLinecap="round" />
                  <path d="M74 102q6 6 12 0" stroke="#42828c" strokeWidth="3" strokeLinecap="round" />
                  <path d="M19 61l-4-8m0 22l-9-3" stroke="#03c7ca" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </Box>
              <Typography
                variant="h3"
                component="p"
                sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: "1.5rem", md: "3rem" } }}
              >
                <Box component="span" sx={{ display: { xs: "block", sm: "none" }, color: "#101d26", mb: 3 }}>Where should we begin?</Box>
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>What can I help you with today?</Box>
              </Typography>
              <Typography variant="h6" component="p" sx={{ display: { xs: "none", sm: "block" }, color: "text.secondary", fontWeight: 400, fontSize: { xs: "0.9375rem", sm: "1.25rem" }, mb: { xs: 2, sm: 5 } }}>
                Ask a question about Noticeboard, or try one of the examples below.
              </Typography>
              {desktop && composer}
              <Box
                sx={{
                  display: { xs: "grid", sm: "flex" },
                  flexWrap: "wrap",
                  justifyContent: "center",
                  mt: { xs: 0, sm: 3 },
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: { xs: 0.5, sm: 2.5 },
                }}
              >
                {examples.map(({ entry, prompt, description }) => (
                  <ExampleCard key={prompt ?? entry.id} entry={entry} prompt={prompt} description={description} circleName={circleName} onAsk={ask} />
                ))}
              </Box>
            </Box>
          ) : (
            <Box
              role="log"
              aria-label="Conversation"
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              {messages.map((m) =>
                m.role === "user" ? (
                  <Box
                    key={m.id}
                    sx={{
                      alignSelf: "flex-end",
                      maxWidth: "80%",
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 4,
                      px: 2.5,
                      py: 1.5,
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                      {m.text}
                    </Typography>
                  </Box>
                ) : (
                  <AssistantMessage
                    key={m.id}
                    message={m}
                    onRevealed={() => onRevealed(m.id)}
                    onOpenLink={goTo}
                    onAsk={ask}
                    onOpenHandoff={(to) => {
                      setDraft("");
                      onOpenHandoff(to);
                    }}
                    // Looks good, and which chips are still live, wait until the replies on their way have heard about the task
                    task={isReplying ? null : task}
                    lastAsked={isReplying ? null : lastAsked}
                    onConfirmTask={(to) => {
                      setDraft("");
                      onConfirmTask(to);
                    }}
                  />
                )
              )}
              {resumeOffer && <ResumeOfferCard record={resumeOffer} onCarryOn={onCarryOn} onStartFresh={onStartFresh} />}
              {isReplying && (
                <Typography
                  role="status"
                  aria-label="Replying"
                  variant="body2"
                  sx={{ color: "text.secondary" }}
                >
                  Replying…
                </Typography>
              )}
              <div ref={endRef} />
            </Box>
          )}
        </Box>
      </Box>

      {(!desktop || messages.length > 0) && composer}
    </Dialog>
  );
};

export default AskNoticeboardModal;
